import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Minus, Truck, Calculator, RotateCcw, ArrowRight } from "lucide-react";
import { volumeCatalogue } from "../mock/extraData";
import Reveal from "../components/Reveal";

const Calculateur = () => {
  const navigate = useNavigate();
  const [quantities, setQuantities] = useState({});
  useEffect(() => window.scrollTo(0, 0), []);

  const setQty = (key, delta) => {
    setQuantities((prev) => {
      const next = Math.max(0, (prev[key] || 0) + delta);
      return { ...prev, [key]: next };
    });
  };

  const totalVolume = useMemo(() => {
    let total = 0;
    volumeCatalogue.forEach((cat) => {
      cat.items.forEach((item) => {
        const key = `${cat.category}-${item.name}`;
        total += (quantities[key] || 0) * item.volume;
      });
    });
    return total;
  }, [quantities]);

  const totalItems = useMemo(
    () => Object.values(quantities).reduce((a, b) => a + b, 0),
    [quantities]
  );

  // Suggest a vehicle based on volume
  const vehicle = useMemo(() => {
    if (totalVolume === 0) return null;
    if (totalVolume <= 12) return "Utilitaire 12 m³";
    if (totalVolume <= 20) return "Camion 20 m³";
    if (totalVolume <= 30) return "Camion 30 m³";
    return "Camion 30 m³ + remorque ou plusieurs rotations";
  }, [totalVolume]);

  const reset = () => setQuantities({});

  return (
    <div className="pt-16 sm:pt-20">
      {/* HERO */}
      <section className="bg-[#1B2A4A] py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center animate-fadeUp">
          <span className="inline-flex items-center gap-2 bg-[#C9A227] text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-wider">
            <Calculator size={14} /> Estimation gratuite
          </span>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-white leading-tight">Calculateur de volume</h1>
          <p className="mt-5 text-lg text-gray-300 max-w-2xl mx-auto">
            Estimez le volume de votre déménagement en quelques clics et découvrez le camion adapté.
          </p>
        </div>
      </section>

      {/* CALCULATOR */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Items list */}
          <div className="lg:col-span-2 space-y-8">
            {volumeCatalogue.map((cat) => (
              <div key={cat.category} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="bg-[#1B2A4A]/5 px-6 py-3 border-b border-gray-100">
                  <h3 className="font-bold text-[#1B2A4A]">{cat.category}</h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {cat.items.map((item) => {
                    const key = `${cat.category}-${item.name}`;
                    const qty = quantities[key] || 0;
                    return (
                      <div key={item.name} className="flex items-center justify-between px-6 py-3">
                        <div>
                          <p className="font-medium text-gray-800 text-sm">{item.name}</p>
                          <p className="text-xs text-gray-400">{item.volume} m³ / unité</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setQty(key, -1)}
                            disabled={qty === 0}
                            className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-[#1B2A4A] hover:bg-gray-50 disabled:opacity-30 transition-colors"
                            aria-label={`Retirer ${item.name}`}
                          >
                            <Minus size={16} />
                          </button>
                          <span className="w-6 text-center font-semibold text-[#1B2A4A]">{qty}</span>
                          <button
                            onClick={() => setQty(key, 1)}
                            className="w-8 h-8 rounded-full bg-[#1B2A4A] text-white flex items-center justify-center hover:bg-[#26406b] transition-colors"
                            aria-label={`Ajouter ${item.name}`}
                          >
                            <Plus size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Summary (sticky) */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white rounded-2xl border border-gray-100 shadow-xl p-6">
              <h3 className="text-lg font-bold text-[#1B2A4A] flex items-center gap-2">
                <Calculator size={20} className="text-[#C9A227]" /> Votre estimation
              </h3>
              <div className="mt-6 text-center bg-[#1B2A4A] rounded-xl py-8">
                <p className="text-sm text-gray-300">Volume total estimé</p>
                <p className="text-5xl font-extrabold text-white mt-1">
                  {totalVolume.toFixed(1)}
                  <span className="text-2xl text-[#C9A227]"> m³</span>
                </p>
                <p className="text-sm text-gray-300 mt-2">{totalItems} objet{totalItems > 1 ? "s" : ""} sélectionné{totalItems > 1 ? "s" : ""}</p>
              </div>

              {vehicle && (
                <div className="mt-4 flex items-center gap-3 bg-[#C9A227]/10 rounded-xl p-4">
                  <Truck size={22} className="text-[#C9A227] shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500">Véhicule recommandé</p>
                    <p className="font-semibold text-[#1B2A4A] text-sm">{vehicle}</p>
                  </div>
                </div>
              )}

              <button
                onClick={() => navigate("/contact")}
                className="mt-6 w-full flex items-center justify-center gap-2 bg-[#C9A227] hover:bg-[#b8931f] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors shadow-sm"
              >
                Obtenir mon devis <ArrowRight size={18} />
              </button>
              <button
                onClick={reset}
                className="mt-3 w-full flex items-center justify-center gap-2 text-gray-500 hover:text-[#1B2A4A] font-medium px-6 py-2 rounded-lg transition-colors text-sm"
              >
                <RotateCcw size={15} /> Réinitialiser
              </button>
              <p className="text-xs text-gray-400 text-center mt-4">
                Estimation indicative. Un conseiller confirmera le volume exact lors du devis.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Calculateur;
